import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import { useLang } from './i18n.js';
import { thb, parseTHB } from './money.js';
import { Overlay, Chip, Avatar } from './ui.jsx';

/**
 * Graduate to Account — the Sales → AE seam.
 *
 * The design makes the before/after explicit at the top, and that is the whole
 * point of the screen: a handover is a change of ownership people negotiate,
 * not a status flag. The three "Sales stays as" options exist because the real
 * answer is usually not "handed over" — the rep who won the client keeps the
 * relationship even when the AE runs the work.
 */
export function GraduateModal({ deal, config, me, onClose, onDone, toast }) {
  const { t } = useLang();
  const [aes, setAes] = useState([]);
  const [f, setF] = useState({
    ae_owner_id: '',
    sales_role_after: 'relationship',
    engagement_type: 'retainer',
    service_start: new Date().toISOString().slice(0, 10),
    contracted: thb(deal.amount_satang).replace('฿', ''),
    review_cadence: 'monthly',
    handover_notes: '',
  });
  const [checklist, setChecklist] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api
      .users({ role: 'ae', entity: deal.entity_tag })
      .then((d) => {
        setAes(d.users);
        // Pre-select the AE already on the account, if there is one — most
        // graduations confirm an existing relationship rather than start one.
        if (d.users.length) set('ae_owner_id', deal.ae_id || d.users[0].id);
      })
      .catch((e) => setError(e.message));

    const key = deal.entity_tag === 'MR' ? 'graduation_checklist_mr' : 'graduation_checklist_btm';
    setChecklist((config[key] || []).map((label) => ({ label, done: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.ae_owner_id) {
      setError('Pick an AE to take it over.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.graduate(deal.id, {
        ae_owner_id: f.ae_owner_id,
        sales_role_after: f.sales_role_after,
        engagement_type: f.engagement_type,
        service_start: f.service_start,
        contracted_satang: parseTHB(f.contracted),
        review_cadence: f.review_cadence,
        checklist,
        handover_notes: f.handover_notes || undefined,
      });
      const ae = aes.find((u) => u.id === f.ae_owner_id);
      toast(t('graduated', { n: ae?.name ?? 'AE' }));
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const salesRoles = [
    ['relationship', t('relationshipOwner')],
    ['upsell_support', t('upsellSupport')],
    ['handed_over', t('fullyHandedOver')],
  ];
  const engagements = [
    ['one_off', t('oneOff')],
    ['retainer', t('retainer')],
    ['media_buy', t('mediaBuy')],
    ['project', t('projectScope')],
  ];
  const cadences = [
    ['weekly', t('weekly')],
    ['monthly', t('monthly')],
    ['quarterly', t('quarterly')],
    ['milestone', t('milestone')],
  ];

  const ae = aes.find((u) => u.id === f.ae_owner_id);

  return (
    <Overlay onClose={onClose} kind="drawer">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="drawer-head" style={{ paddingBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 19 }}>
                {t('graduateTitle')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                {deal.company_name} · {deal.title} · {thb(deal.amount_satang)}
              </div>
            </div>
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          {/* Before / after, stated plainly. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: 10,
              marginTop: 14,
              padding: 12,
              borderRadius: 'var(--r-md)',
              background: 'var(--sunken)',
              fontSize: 12.5,
            }}
          >
            <div>
              <div style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 700 }}>{t('was')}</div>
              <div style={{ fontWeight: 600 }}>{deal.owner_name || '—'}</div>
              <div style={{ color: 'var(--ink-3)' }}>{t('salesOwns')}</div>
            </div>
            <div style={{ color: 'var(--ink-3)', fontSize: 16 }}>→</div>
            <div>
              <div style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 700 }}>
                {t('becomes')}
              </div>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                {ae && <Avatar initials={ae.avatar_initials} seed={ae.avatar_var} size={18} />}
                {ae?.name ?? '—'}
              </div>
              <div style={{ color: 'var(--ink-3)' }}>{t('aeOwnsNow')}</div>
            </div>
          </div>
        </div>

        <div className="drawer-body" style={{ paddingTop: 14 }}>
          <label className="field-label">{t('aeTakingOver')}</label>
          <select
            className="select"
            value={f.ae_owner_id}
            onChange={(e) => set('ae_owner_id', e.target.value)}
          >
            <option value="">—</option>
            {aes.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          {aes.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 6 }}>
              Nobody has {deal.entity_tag} AE access yet — an admin has to grant it first.
            </div>
          )}

          <Group label={t('salesStaysAs')}>
            {salesRoles.map(([v, label]) => (
              <Chip key={v} on={f.sales_role_after === v} onClick={() => set('sales_role_after', v)}>
                {label}
              </Chip>
            ))}
          </Group>

          <Group label={t('engagementType')}>
            {engagements.map(([v, label]) => (
              <Chip key={v} on={f.engagement_type === v} onClick={() => set('engagement_type', v)}>
                {label}
              </Chip>
            ))}
          </Group>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16 }}>
            <div>
              <label className="field-label">{t('serviceStart')}</label>
              <input
                className="input"
                type="date"
                value={f.service_start}
                onChange={(e) => set('service_start', e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">{t('contractedValue')}</label>
              <input
                className="input"
                value={f.contracted}
                onChange={(e) => set('contracted', e.target.value)}
                inputMode="decimal"
              />
            </div>
          </div>

          <Group label={t('reviewCadence')}>
            {cadences.map(([v, label]) => (
              <Chip key={v} on={f.review_cadence === v} onClick={() => set('review_cadence', v)}>
                {label}
              </Chip>
            ))}
          </Group>

          <div style={{ marginTop: 18 }}>
            <label className="field-label">{t('handoverChecklist')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {checklist.map((item, i) => (
                <label
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '7px 8px',
                    borderRadius: 'var(--r-sm)',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() =>
                      setChecklist((cl) =>
                        cl.map((x, j) => (j === i ? { ...x, done: !x.done } : x)),
                      )
                    }
                    style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
                  />
                  <span style={{ color: item.done ? 'var(--ink-3)' : 'var(--ink)' }}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
            {/* Unticked items are allowed on purpose: a handover often happens
                before every box is ticked, and blocking it would just push the
                team back to doing it in chat. */}
          </div>

          <div style={{ marginTop: 16 }}>
            <label className="field-label">{t('handoverNotes')}</label>
            <textarea
              className="textarea"
              rows={3}
              value={f.handover_notes}
              onChange={(e) => set('handover_notes', e.target.value)}
            />
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 14 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot">
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy || !f.ae_owner_id}>
            {t('graduateTitle')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

const Group = ({ label, children }) => (
  <div style={{ marginTop: 16 }}>
    <label className="field-label">{label}</label>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div>
  </div>
);
